import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { IconCheck, IconPlayerPause, IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useCreateAssetAnnouncement,
  useDeleteAssetAnnouncement,
  usePublishAssetAnnouncement,
  useUnpublishAssetAnnouncement,
} from 'src/Hooks/Mutations/useAssetAnnouncementMutations';
import useAssetAnnouncements from 'src/Hooks/Queries/useAssetAnnouncements';
import { AnnouncementLevel } from 'src/types/announcement.types';

import styles from './AssetAnnouncementsModal.module.scss';
import { Props } from './AssetAnnouncementsModal.types';

const LEVEL_BADGE_COLORS: Record<AnnouncementLevel, string> = {
  [AnnouncementLevel.Info]: 'blue',
  [AnnouncementLevel.Warning]: 'yellow',
  [AnnouncementLevel.Critical]: 'red',
};

const LEVEL_TRANSLATION_KEYS: Record<AnnouncementLevel, string> = {
  [AnnouncementLevel.Info]: 'admin.announcements.levelInfo',
  [AnnouncementLevel.Warning]: 'admin.announcements.levelWarning',
  [AnnouncementLevel.Critical]: 'admin.announcements.levelCritical',
};

const AssetAnnouncementsModal = ({ open, onClose }: Props) => {
  const { t } = useTranslation();
  const { data: announcements } = useAssetAnnouncements();
  const { mutate: createAnnouncement, isLoading: isCreating } = useCreateAssetAnnouncement();
  const { mutate: publishAnnouncement } = usePublishAssetAnnouncement();
  const { mutate: unpublishAnnouncement } = useUnpublishAssetAnnouncement();
  const { mutate: deleteAnnouncement } = useDeleteAssetAnnouncement();

  const [draft, setDraft] = useState('');
  const [level, setLevel] = useState<AnnouncementLevel>(AnnouncementLevel.Info);

  const handleCreate = (publish: boolean) => {
    const message = draft.trim();
    if (!message) return;
    createAnnouncement(
      { message, level, publish },
      {
        onSuccess: () => setDraft(''),
      }
    );
  };

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={t('admin.announcements.title', "Bandeau d'information")}
      size="lg"
    >
      <Text size="sm" c="dimmed" mb="md">
        {t(
          'admin.announcements.description',
          'Publiez un message à destination de tous les membres du bien. Un seul bandeau peut être actif à la fois.'
        )}
      </Text>

      <div className={styles.composer}>
        <SegmentedControl
          fullWidth
          value={level}
          onChange={value => setLevel(value as AnnouncementLevel)}
          mb="xs"
          data={[
            {
              value: AnnouncementLevel.Info,
              label: t(LEVEL_TRANSLATION_KEYS[AnnouncementLevel.Info], 'Info'),
            },
            {
              value: AnnouncementLevel.Warning,
              label: t(LEVEL_TRANSLATION_KEYS[AnnouncementLevel.Warning], 'Avertissement'),
            },
            {
              value: AnnouncementLevel.Critical,
              label: t(LEVEL_TRANSLATION_KEYS[AnnouncementLevel.Critical], 'Critique'),
            },
          ]}
        />
        <Textarea
          value={draft}
          onChange={e => setDraft(e.currentTarget.value)}
          placeholder={t(
            'admin.announcements.placeholder',
            'Saisissez votre message (500 caractères max)...'
          )}
          autosize
          minRows={2}
          maxRows={6}
          maxLength={500}
        />
        <Group justify="flex-end" mt="xs">
          <Button
            variant="outline"
            onClick={() => handleCreate(false)}
            loading={isCreating}
            disabled={!draft.trim()}
            leftSection={<IconPlus size={16} />}
          >
            {t('admin.announcements.saveDraft', 'Créer (brouillon)')}
          </Button>
          <Button
            onClick={() => handleCreate(true)}
            loading={isCreating}
            disabled={!draft.trim()}
            leftSection={<IconCheck size={16} />}
          >
            {t('admin.announcements.createAndPublish', 'Créer et publier')}
          </Button>
        </Group>
      </div>

      <Stack gap="sm" mt="lg">
        {announcements?.length === 0 && (
          <Text size="sm" c="dimmed">
            {t('admin.announcements.empty', 'Aucun bandeau pour le moment.')}
          </Text>
        )}
        {announcements?.map(announcement => (
          <div key={announcement.id} className={styles.announcementItem}>
            <div className={styles.announcementContent}>
              <Group gap="xs" mb={4}>
                <Badge color={LEVEL_BADGE_COLORS[announcement.level]} size="sm" variant="light">
                  {t(LEVEL_TRANSLATION_KEYS[announcement.level], announcement.level)}
                </Badge>
                {announcement.isActive && (
                  <Badge color="green" size="sm" variant="filled">
                    {t('admin.announcements.active', 'Publié')}
                  </Badge>
                )}
              </Group>
              <Text size="sm">{announcement.message}</Text>
            </div>
            <Group gap="xs">
              {announcement.isActive ? (
                <ActionIcon
                  variant="subtle"
                  color="orange"
                  onClick={() => unpublishAnnouncement(announcement.id)}
                  aria-label={t('admin.announcements.unpublish', 'Dépublier')}
                >
                  <IconPlayerPause size={18} />
                </ActionIcon>
              ) : (
                <ActionIcon
                  variant="subtle"
                  color="green"
                  onClick={() => publishAnnouncement(announcement.id)}
                  aria-label={t('admin.announcements.publish', 'Publier')}
                >
                  <IconCheck size={18} />
                </ActionIcon>
              )}
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => deleteAnnouncement(announcement.id)}
                aria-label={t('admin.announcements.delete', 'Supprimer')}
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Group>
          </div>
        ))}
      </Stack>
    </Modal>
  );
};

export default AssetAnnouncementsModal;
